import React, { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { dateTime } from "@/lib/format";
import { btnPrimary, btnGhost } from "@/components/Brand";
import { DialogShell } from "@/components/Dialog";
import { OverrideFields } from "@/components/platform/OverrideFields";

/** A past expiry means the studio is locked out, not merely trialing. */
const statusLabel = (sub, t) => {
  if (!sub) return "—";
  if (sub.current_period_end && new Date(sub.current_period_end) < new Date()) return t("locked_out");
  if (sub.status === "lifetime") return t("sub_status_lifetime");
  return t(`sub_status_${sub.status}`);
};

/** Developer override: switch a studio's plan, inject days or lock it out. */
export const SubscriptionOverride = ({ tenant, plans, onClose, onSaved }) => {
  const { t } = useI18n();
  const [status, setStatus] = useState(tenant.subscription?.status === "lifetime" ? "lifetime" : "active");
  const [planId, setPlanId] = useState(tenant.subscription?.plan_id || "");
  const [days, setDays] = useState(30);
  const [fromNow, setFromNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/platform/tenants/${tenant.tenant_id}/subscription`, {
        status,
        plan_id: planId || null,
        days_delta: Number(days) || 0,
        from_now: fromNow,
      });
      setResult(res.data);
      toast.success(t("override_saved"));
      await onSaved();
    } catch (err) {
      toast.error(apiError(err, "Could not update subscription"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell onClose={onClose} testId="subscription-override-dialog">
      <p className="eyebrow text-[#D4AF37]">{t("subscription_override")}</p>
      <h2 className="mt-3 text-2xl tracking-tight">{tenant.name}</h2>
      <p className="mt-2 font-mono-accent text-[10px] tracking-[0.16em] uppercase text-[#707070]"
        data-testid="override-current-status">
        {statusLabel(tenant.subscription, t)} · {tenant.subscription?.current_period_end
          ? dateTime(tenant.subscription.current_period_end) : "∞"}
      </p>
      <p className="mt-5 text-[11px] text-[#707070] leading-relaxed">{t("override_hint")}</p>

      <OverrideFields
        status={status} setStatus={setStatus}
        planId={planId} setPlanId={setPlanId}
        days={days} setDays={setDays}
        fromNow={fromNow} setFromNow={setFromNow}
        plans={plans}
      />

      {result && (
        <p className="mt-6 border-t border-[#1E1E1E] pt-5 text-xs text-[#D4AF37]" data-testid="override-result">
          {statusLabel(result, t)} · {t("new_expiry")}: {result.current_period_end ? dateTime(result.current_period_end) : "∞"}
        </p>
      )}

      <div className="mt-7 flex flex-wrap gap-3">
        <button data-testid="override-save-btn" disabled={busy} onClick={save} className={btnPrimary}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("set_subscription")}
        </button>
        <button data-testid="override-cancel-btn" onClick={onClose} className={btnGhost}>{t("cancel")}</button>
      </div>
    </DialogShell>
  );
};
